export interface BuilderHappyHourPeriod {
  startDay: number;
  endDay: number;
  startTime: { hours: number; minutes: number };
  endTime: { hours: number; minutes: number };
}

export interface BuilderHappyHourTimes {
  nextStart: number | null;
  currentEnd: number | null;
}

interface Range { start: number; end: number }

const MINUTES_PER_DAY = 24 * 60;
const MINUTES_PER_WEEK = 7 * MINUTES_PER_DAY;

function isIntegerBetween(value: number, min: number, max: number): boolean {
  return Number.isInteger(value) && value >= min && value <= max;
}

function weeklyRange(period: BuilderHappyHourPeriod): Range | null {
  if (!isIntegerBetween(period?.startDay, 1, 7) || !isIntegerBetween(period?.endDay, 1, 7)
    || !isIntegerBetween(period?.startTime?.hours, 0, 23) || !isIntegerBetween(period?.startTime?.minutes, 0, 59)
    || !isIntegerBetween(period?.endTime?.hours, 0, 23) || !isIntegerBetween(period?.endTime?.minutes, 0, 59)) {
    return null;
  }
  const start = (period.startDay - 1) * MINUTES_PER_DAY + period.startTime.hours * 60 + period.startTime.minutes;
  let end = (period.endDay - 1) * MINUTES_PER_DAY + period.endTime.hours * 60 + period.endTime.minutes;
  if (end <= start) end += MINUTES_PER_WEEK;
  return { start, end };
}

function mergeRanges(ranges: readonly Range[]): Range[] {
  const merged: Range[] = [];
  for (const range of [...ranges].sort((left, right) => left.start - right.start)) {
    const previous = merged.at(-1);
    if (previous && range.start <= previous.end) previous.end = Math.max(previous.end, range.end);
    else merged.push({ ...range });
  }
  return merged;
}

function coversWholeWeek(ranges: readonly Range[]): boolean {
  const segments = ranges.flatMap(({ start, end }) => end > MINUTES_PER_WEEK
    ? [{ start, end: MINUTES_PER_WEEK }, { start: 0, end: end - MINUTES_PER_WEEK }]
    : [{ start, end }]);
  const merged = mergeRanges(segments);
  return merged.length === 1 && merged[0].start === 0 && merged[0].end === MINUTES_PER_WEEK;
}

/** Construct local calendar dates so a week across DST is not assumed to be 168 hours. */
function occurrenceTime(now: Date, minuteOfWeek: number, weekOffset: number): number {
  const date = new Date(now);
  const weekday = (now.getDay() + 6) % 7;
  date.setDate(now.getDate() - weekday + weekOffset * 7 + Math.floor(minuteOfWeek / MINUTES_PER_DAY));
  const minuteOfDay = minuteOfWeek % MINUTES_PER_DAY;
  date.setHours(Math.floor(minuteOfDay / 60), minuteOfDay % 60, 0, 0);
  return date.getTime();
}

/** Resolve schedule data independently of the service's cached Now/Next status. */
export function getBuilderHappyHourTimes(
  periods: readonly BuilderHappyHourPeriod[], nowMs: number,
): BuilderHappyHourTimes {
  const now = new Date(nowMs);
  if (!Array.isArray(periods) || !Number.isFinite(nowMs) || !Number.isFinite(now.getTime())) {
    return { nextStart: null, currentEnd: null };
  }
  const ranges = periods.map(weeklyRange).filter((range): range is Range => range !== null);
  const occurrences: Range[] = [];
  for (const range of ranges) {
    for (const week of [-1, 0, 1]) {
      const start = occurrenceTime(now, range.start, week);
      const end = occurrenceTime(now, range.end, week);
      if (Number.isFinite(start) && Number.isFinite(end) && end > start) occurrences.push({ start, end });
    }
  }
  const futureStarts = occurrences.filter(range => range.start > nowMs).map(range => range.start);
  const active = mergeRanges(occurrences).find(range => range.start <= nowMs && nowMs < range.end);
  return {
    nextStart: futureStarts.length ? Math.min(...futureStarts) : null,
    // A fully covered weekly cycle has no finite end, even at the enumeration horizon.
    currentEnd: active && !coversWholeWeek(ranges) ? active.end : null,
  };
}
